import { clearSession, getAccessToken } from '../auth/session';
import type {
  AccessLevel,
  ChildListingResponse,
  ErrorResponse,
  GrantRole,
  OperationRequest,
  PlanListingResponse,
  PlanResponse,
  PublicMode,
  RealtimeConnectionResponse,
  RegisterPushDeviceRequest,
  ShareLinkResponse,
  SharedPlanResponse,
  SharingResponse,
} from './types';

/** Thrown for any non-2xx response; carries the backend's domain error contract (ADR-002). */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly body: ErrorResponse,
  ) {
    super(`${body.code}: ${body.description}`);
  }
}

/** A plan document together with the caller's effective access to it, and its name. */
export interface OpenedPlan {
  plan: PlanResponse;
  access: AccessLevel;
  /**
   * The plan's display name from `X-Resource-Name` — the node's name as the resource
   * middleware resolved it for this very response, so a plan opened from a bare id (a push
   * notice, a `?plan=` link, a pasted id) is titled in the same round trip. Null where a host
   * stamps none, a blank, or a value that is not valid percent-encoding — the header only
   * titles the screen, it never fails an open.
   */
  name: string | null;
}

/**
 * What an install binds the client to (ADR-029 §2): where the API is (`/api` behind the SWA on
 * the web, an absolute `https://…/api` inside a native shell), how to reach it, where the
 * platform bearer token comes from, and what to do when the API no longer honours it.
 */
export interface ClientDeps {
  readonly baseUrl: string;
  readonly fetch: (input: string, init?: RequestInit) => Promise<Response>;
  readonly getToken: () => string | null;
  readonly onUnauthorized: () => void;
}

/** The planlets API (ADR-025 §2 / ADR-027 / ADR-028) as one bound client. */
export interface PlanletsClient {
  /**
   * Creates a new planlet under a server-generated id (ADR-027 §7 — the client-chosen-id PUT
   * is gone) and returns its empty document.
   */
  createPlan(name: string): Promise<PlanResponse>;
  /** Reads a publicly shared plan through its share link (anonymous — ADR-027 §6). */
  getSharedPlan(shareId: string): Promise<SharedPlanResponse>;
  /** Reads the current document. */
  getPlan(planId: string): Promise<PlanResponse>;
  /**
   * Applies one editing operation server-authoritatively and returns the resulting document
   * (an unchanged version means the operation was a stale/redelivered no-op).
   */
  applyOperation(planId: string, operation: OperationRequest): Promise<PlanResponse>;
  /** Removes the plan (204) — the restorable soft delete under enforcement (ADR-027 §3). */
  deletePlan(planId: string): Promise<void>;
  /**
   * Negotiates a receive-only realtime subscription for a plan (ADR-028 §4): the WebSocket URL
   * to open and when its credential stops being accepted for connecting. Answers 404
   * `Realtime.Unavailable` where no realtime transport is configured (local runs).
   */
  negotiateRealtime(planId: string): Promise<RealtimeConnectionResponse>;
  /** Opens a plan: the document, the caller's effective access to it, and its name. */
  openPlan(planId: string): Promise<OpenedPlan>;
  /** The caller's home screen: every plan they own or hold a grant on, newest first (ADR-027 §5). */
  listPlans(): Promise<PlanListingResponse[]>;
  /** The live sub-planlets under a plan, with the caller's effective access to each. */
  listChildren(planId: string): Promise<ChildListingResponse[]>;
  /** Creates a sub-planlet under `parentId` (≥ Write on the parent) — its empty document. */
  createChildPlan(parentId: string, name: string): Promise<PlanResponse>;
  /** "Make it mine" (ADR-027 §7): copies the plan's content into a fresh root the caller owns. */
  importPlan(planId: string, name: string): Promise<PlanResponse>;
  /** Renames the plan (≥ Write). */
  renamePlan(planId: string, name: string): Promise<void>;
  /**
   * Re-parents the plan under `parentId`, or un-nests it to the top level with null (Owner on
   * the moved plan — ADR-027 §4; the server refuses cycles, nesting past the depth cap and
   * destinations the caller cannot write). A move to the current parent is a server-side no-op.
   */
  movePlan(planId: string, parentId: string | null): Promise<void>;
  /** Undoes a soft delete within the retention window (the owner's tombstone, ADR-027 §3). */
  restorePlan(planId: string): Promise<void>;
  /**
   * The owner's view of a plan's §3 aggregate: tree position (`parentId`), owner, grants,
   * public mode, share id.
   */
  getSharing(planId: string): Promise<SharingResponse>;
  /** Sets a grant (`none` blocks inheritance for that principal — ADR-027 §3). */
  setGrant(planId: string, email: string, role: GrantRole): Promise<void>;
  /** Clears a grant — the owner removes anyone, a grantee removes themself (leaves). */
  removeGrant(planId: string, email: string): Promise<void>;
  /** Sets the public mode; answers the share id (minted on `public`, null otherwise). */
  setPublic(planId: string, mode: PublicMode): Promise<ShareLinkResponse>;
  /** Mints a fresh share id, retiring the old link outright (ADR-027 §6). */
  rotateShareLink(planId: string): Promise<ShareLinkResponse>;
  /**
   * Negotiates the anonymous realtime subscription behind a share link (ADR-028 §4) — for the
   * linked plan, or for the descendant `nodeId` under it (chain-checked like the node doorway).
   */
  negotiateSharedRealtime(shareId: string, nodeId?: string): Promise<RealtimeConnectionResponse>;
  /** A descendant of a shared plan through its share link (anonymous, chain-checked). */
  getSharedNode(shareId: string, nodeId: string): Promise<SharedPlanResponse>;
  /** The public sub-planlets under a share link's resource, or under `nodeId` beneath it. */
  getSharedChildren(shareId: string, nodeId?: string): Promise<ChildListingResponse[]>;
  /**
   * Registers (or re-registers) this install's device for push notices (ADR-030 §2): the push
   * service the OS handed a token from, and the token. Scoped to the signed-in principal.
   */
  registerPushDevice(installationId: string, device: RegisterPushDeviceRequest): Promise<void>;
  /** Forgets this install's device — what a sign-out does (ADR-030 §2). */
  unregisterPushDevice(installationId: string): Promise<void>;
}

/**
 * The `X-Access-Level` the resource middleware stamps (ADR-027 §7). Absent — a host running
 * without the resource layer — reads as full access: the server is the authority either way,
 * the header only shapes the UI.
 */
function accessLevelOf(res: Response): AccessLevel {
  const raw = res.headers.get('X-Access-Level');
  return raw === 'write' || raw === 'read' || raw === 'publicRead' || raw === 'none'
    ? raw
    : 'owner';
}

/**
 * The `X-Resource-Name` the resource middleware stamps beside `X-Access-Level`: the route
 * resource's display name, RFC 3986 percent-encoded (`Uri.EscapeDataString`) so any Unicode
 * name rides an ASCII field value. Decoded with `decodeURIComponent` — never `URLSearchParams`,
 * a `+` must stay a `+` (.NET emits `%2B`). Absent, blank or undecodable reads as unknown; no
 * client-side length cap — that is the server's invariant.
 */
function resourceNameOf(res: Response): string | null {
  const raw = res.headers.get('X-Resource-Name');
  if (raw === null) return null;
  try {
    const name = decodeURIComponent(raw).trim();
    return name === '' ? null : name;
  } catch {
    return null; // a malformed escape (URIError) — no name, not a failed open
  }
}

/** Binds the API to one install's transport, base URL and token source (ADR-029 §2). */
export function createClient(deps: ClientDeps): PlanletsClient {
  const { baseUrl } = deps;

  /** Every API call carries the platform bearer token while a session is alive (ADR-024). */
  function authHeaders(extra: Record<string, string> = {}): Record<string, string> {
    const token = deps.getToken();
    return token === null ? extra : { ...extra, Authorization: `Bearer ${token}` };
  }

  async function rejectUnlessOk(res: Response): Promise<void> {
    if (!res.ok) {
      // The backend re-validates every call; a 401 means the session is no longer honoured
      // (expired/invalid) — the install drops it so the app returns to sign-in.
      if (res.status === 401) deps.onUnauthorized();
      throw new ApiError(res.status, (await res.json()) as ErrorResponse);
    }
  }

  async function readJson<T>(res: Response): Promise<T> {
    await rejectUnlessOk(res);
    return (await res.json()) as T;
  }

  const json = (body: unknown) => ({
    method: 'POST',
    headers: authHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(body),
  });

  return {
    async createPlan(name) {
      return readJson(await deps.fetch(`${baseUrl}/plans`, json({ name })));
    },

    async getSharedPlan(shareId) {
      return readJson(
        await deps.fetch(`${baseUrl}/shares/${encodeURIComponent(shareId)}`, {
          headers: authHeaders(),
        }),
      );
    },

    async getPlan(planId) {
      return readJson(
        await deps.fetch(`${baseUrl}/plans/${encodeURIComponent(planId)}`, {
          headers: authHeaders(),
        }),
      );
    },

    async applyOperation(planId, operation) {
      return readJson(
        await deps.fetch(
          `${baseUrl}/plans/${encodeURIComponent(planId)}/operations`,
          json(operation),
        ),
      );
    },

    async deletePlan(planId) {
      await rejectUnlessOk(
        await deps.fetch(`${baseUrl}/plans/${encodeURIComponent(planId)}`, {
          method: 'DELETE',
          headers: authHeaders(),
        }),
      );
    },

    async negotiateRealtime(planId) {
      return readJson(
        await deps.fetch(`${baseUrl}/plans/${encodeURIComponent(planId)}/realtime`, {
          headers: authHeaders(),
        }),
      );
    },

    async openPlan(planId) {
      const res = await deps.fetch(`${baseUrl}/plans/${encodeURIComponent(planId)}`, {
        headers: authHeaders(),
      });
      const plan = await readJson<PlanResponse>(res);
      return { plan, access: accessLevelOf(res), name: resourceNameOf(res) };
    },

    async listPlans() {
      return readJson(await deps.fetch(`${baseUrl}/plans`, { headers: authHeaders() }));
    },

    async listChildren(planId) {
      return readJson(
        await deps.fetch(`${baseUrl}/plans/${encodeURIComponent(planId)}/children`, {
          headers: authHeaders(),
        }),
      );
    },

    async createChildPlan(parentId, name) {
      return readJson(
        await deps.fetch(
          `${baseUrl}/plans/${encodeURIComponent(parentId)}/children`,
          json({ name }),
        ),
      );
    },

    async importPlan(planId, name) {
      return readJson(
        await deps.fetch(`${baseUrl}/plans/${encodeURIComponent(planId)}/import`, json({ name })),
      );
    },

    async renamePlan(planId, name) {
      await rejectUnlessOk(
        await deps.fetch(`${baseUrl}/plans/${encodeURIComponent(planId)}`, {
          method: 'PATCH',
          headers: authHeaders({ 'Content-Type': 'application/json' }),
          body: JSON.stringify({ name }),
        }),
      );
    },

    async movePlan(planId, parentId) {
      await rejectUnlessOk(
        await deps.fetch(
          `${baseUrl}/plans/${encodeURIComponent(planId)}/move`,
          json({ parentId }),
        ),
      );
    },

    async restorePlan(planId) {
      await rejectUnlessOk(
        await deps.fetch(`${baseUrl}/plans/${encodeURIComponent(planId)}/restore`, {
          method: 'POST',
          headers: authHeaders(),
        }),
      );
    },

    async getSharing(planId) {
      return readJson(
        await deps.fetch(`${baseUrl}/plans/${encodeURIComponent(planId)}/sharing`, {
          headers: authHeaders(),
        }),
      );
    },

    async setGrant(planId, email, role) {
      await rejectUnlessOk(
        await deps.fetch(
          `${baseUrl}/plans/${encodeURIComponent(planId)}/grants/${encodeURIComponent(email)}`,
          {
            method: 'PUT',
            headers: authHeaders({ 'Content-Type': 'application/json' }),
            body: JSON.stringify({ role }),
          },
        ),
      );
    },

    async removeGrant(planId, email) {
      await rejectUnlessOk(
        await deps.fetch(
          `${baseUrl}/plans/${encodeURIComponent(planId)}/grants/${encodeURIComponent(email)}`,
          { method: 'DELETE', headers: authHeaders() },
        ),
      );
    },

    async setPublic(planId, mode) {
      return readJson(
        await deps.fetch(`${baseUrl}/plans/${encodeURIComponent(planId)}/public`, {
          method: 'PUT',
          headers: authHeaders({ 'Content-Type': 'application/json' }),
          body: JSON.stringify({ mode }),
        }),
      );
    },

    async rotateShareLink(planId) {
      return readJson(
        await deps.fetch(`${baseUrl}/plans/${encodeURIComponent(planId)}/share-link/rotate`, {
          method: 'POST',
          headers: authHeaders(),
        }),
      );
    },

    async negotiateSharedRealtime(shareId, nodeId) {
      const query = nodeId === undefined ? '' : `?node=${encodeURIComponent(nodeId)}`;
      return readJson(
        await deps.fetch(`${baseUrl}/shares/${encodeURIComponent(shareId)}/realtime${query}`, {
          headers: authHeaders(),
        }),
      );
    },

    async getSharedNode(shareId, nodeId) {
      return readJson(
        await deps.fetch(
          `${baseUrl}/shares/${encodeURIComponent(shareId)}/nodes/${encodeURIComponent(nodeId)}`,
          { headers: authHeaders() },
        ),
      );
    },

    async getSharedChildren(shareId, nodeId) {
      const query = nodeId === undefined ? '' : `?node=${encodeURIComponent(nodeId)}`;
      return readJson(
        await deps.fetch(`${baseUrl}/shares/${encodeURIComponent(shareId)}/children${query}`, {
          headers: authHeaders(),
        }),
      );
    },

    async registerPushDevice(installationId, device) {
      await rejectUnlessOk(
        await deps.fetch(`${baseUrl}/push/devices/${encodeURIComponent(installationId)}`, {
          method: 'PUT',
          headers: authHeaders({ 'Content-Type': 'application/json' }),
          body: JSON.stringify(device),
        }),
      );
    },

    async unregisterPushDevice(installationId) {
      await rejectUnlessOk(
        await deps.fetch(`${baseUrl}/push/devices/${encodeURIComponent(installationId)}`, {
          method: 'DELETE',
          headers: authHeaders(),
        }),
      );
    },
  };
}

/**
 * The browser install's client: same-origin `/api` behind the SWA (or `VITE_API_BASE`), the
 * page's `fetch`, and the in-memory session as the token source — a 401 clears it.
 */
export const webClient: PlanletsClient = createClient({
  baseUrl: import.meta.env.VITE_API_BASE ?? '/api',
  fetch: (input, init) => fetch(input, init),
  getToken: getAccessToken,
  onUnauthorized: clearSession,
});

export const {
  createPlan,
  getSharedPlan,
  getPlan,
  applyOperation,
  deletePlan,
  negotiateRealtime,
  openPlan,
  listPlans,
  listChildren,
  createChildPlan,
  importPlan,
  renamePlan,
  movePlan,
  restorePlan,
  getSharing,
  setGrant,
  removeGrant,
  setPublic,
  rotateShareLink,
  negotiateSharedRealtime,
  getSharedNode,
  getSharedChildren,
  registerPushDevice,
  unregisterPushDevice,
} = webClient;
